import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue';
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, mergeProps as _mergeProps, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, Fragment as _Fragment, createElementBlock as _createElementBlock, isRef as _isRef, withCtx as _withCtx } from "vue";
const _hoisted_1 = { class: "flex flex-col gap-0.5" };
const _hoisted_2 = ["id"];
const _hoisted_3 = ["id"];
const _hoisted_4 = { class: "text-sm font-normal leading-5 text-(--text-text-heading) dark:text-(--text-text-white-default)" };
const _hoisted_5 = { class: "text-sm font-normal leading-5 text-(--text-text-heading) dark:text-(--text-text-white-default)" };
const _hoisted_6 = {
    class: "flex flex-col gap-4 py-1",
    "data-testid": "subscription-create-modal"
};
const _hoisted_7 = { class: "grid grid-cols-1 sm:grid-cols-2 gap-4" };
const _hoisted_8 = { class: "border border-(--colors-border-base-soft) rounded-xl p-5 flex flex-col" };
const _hoisted_9 = { class: "font-semibold text-(--text-text-heading) dark:text-(--text-text-white-default) mb-5" };
const _hoisted_10 = { class: "border border-(--colors-border-base-soft) rounded-xl p-5 flex flex-col" };
const _hoisted_11 = { class: "font-semibold text-(--text-text-heading) dark:text-(--text-text-white-default) mb-5" };
const _hoisted_12 = { class: "border border-(--colors-border-base-soft) rounded-xl p-5" };
const _hoisted_13 = { class: "grid grid-cols-1 sm:grid-cols-2 gap-x-4" };
import { computed, reactive, ref, toRef, useId, watch } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { useDebounceFn } from "@vueuse/core";
import { toDatePickerFormat } from "@/shared/utils/date-formatting";
import { useDateIsoBinding } from "@/shared/composables";
import { useProfileStore } from "@/modules/settings/stores/useProfileStore";
import { AutoComplete, AutoCompleteAllResultsModal, Button, DatePickerInput, Modal, NumberInput, SelectInput, TextInput, } from "@eudonet/opal";
import { buildAutoCompleteLabels } from "@/shared/composables/autoCompleteLabels";
import { useSubscriptionStore } from "../../stores/useSubscriptionStore";
import { useDonationStore } from "../../stores/useDonationStore";
import { useAppToast } from "@/shared/composables/useAppToast";
import { useDonationOptions } from "../composables/useDonationOptions";
import { useDonorReceiptInformation } from "../composables/useDonorReceiptInformation";
import { describeDonorInformationGap, parseDonorInformationGaps, } from "../../domain/entities/DonorReceiptInformation";
import { getApiErrorCode, getApiErrorResult } from "@/infrastructure/services/ApiErrorService";
import { useDisplayLocale } from "@/shared/composables/useDisplayLocale";
import { useWorkspaceCurrency } from "@/shared/composables/useWorkspaceCurrency";
import { currencySymbol } from "@/shared/utils/workspaceLocaleLabels";
import DonorReceiptInformationNotice from "./DonorReceiptInformationNotice.vue";
import { getContacts } from "@/modules/contact/infrastructure/adapters/contactService";
import { getOrganisations, searchOrganisations, } from "@/modules/organisation/infrastructure/adapters/organisationService";
import { useCampaignOptions } from "../composables/useCampaignOptions";
import CreateOrganisationModal from "@/modules/organisation/presentation/components/CreateOrganisationModal.vue";
import CreateContactModal from "@/modules/contact/presentation/components/CreateContactModal.vue";
// Fragment root (Modal + sibling sub-modals) → Vue can't auto-inherit fallthrough
// attrs and warns on any passed attribute; the Modal binds its props explicitly, so
// disabling inheritance is behaviour-neutral and silences the warning (parity with
// DonationCreateModal).
const ORGS_FETCH_PAGE_SIZE = 100;
const ALL_ORGS_FETCH_MAX = 500;
const CONTACTS_FETCH_PAGE_SIZE = 100;
const ALL_CONTACTS_FETCH_MAX = 500;
export default /*@__PURE__*/ _defineComponent({
    ...{ inheritAttrs: false },
    __name: 'SubscriptionCreateModal',
    props: /*@__PURE__*/ _mergeModels({
        lockedDonor: {}
    }, {
        "modelValue": { type: Boolean, ...{ default: false } },
        "modelModifiers": {},
    }),
    emits: /*@__PURE__*/ _mergeModels(["close", "created"], ["update:modelValue"]),
    setup(__props, { emit: __emit }) {
        const open = _useModel(__props, "modelValue");
        const props = __props;
        const emit = __emit;
        const router = useRouter();
        const { t, locale } = useI18n();
        const profileStore = useProfileStore();
        const store = useSubscriptionStore();
        // Accessible name/description for the dialog: OPAL Modal renders role="dialog"
        // and forwards fallthrough attrs onto that container, so aria-labelledby /
        // aria-describedby must be wired here (mirrors CreateContactModal /
        // CreateOrganisationModal). Without them the dialog announces with no name.
        const titleId = useId();
        const descriptionId = useId();
        // OPAL DatePickerInput speaks the user's display format ("dd/mm/yyyy" in fr-FR,
        // "mm/dd/yyyy" by default) in both directions. We keep the wire format ISO
        // (yyyy-MM-dd) on `form.startDate` / `form.endDate`, and bridge to/from the
        // picker via `useDateIsoBinding`. Without this bridge the picker emits
        // "05/27/2026" which the BFF rejects with `JSON value could not be converted
        // to System.DateTime` on the POST /subscriptions payload.
        const dateFormat = computed(() => toDatePickerFormat(profileStore.profile?.dateFormat, locale.value));
        // donationStore owns the BFF catalogs. Loaded so donor type / donation source
        // label translations stay aligned with what OPDON exposes.
        const donationStore = useDonationStore();
        const { toastSuccess, toastError } = useAppToast();
        // The amount is typed and stored in the workspace reference currency. Read again on every open:
        // the modal stays mounted while closed.
        const { currency: workspaceCurrency, isUnavailable: workspaceCurrencyUnavailable, load: loadWorkspaceCurrency, } = useWorkspaceCurrency({ immediate: false });
        watch(open, (isOpen) => {
            if (isOpen)
                void loadWorkspaceCurrency();
        }, { immediate: true });
        const displayLocale = useDisplayLocale();
        const amountPrefix = computed(() => workspaceCurrency.value ? currencySymbol(workspaceCurrency.value, displayLocale.value) : "");
        const { donorTypeOptions, donationNatureOptions, donationFormOptions } = useDonationOptions();
        const modePaiementOptions = computed(() => [
            { value: "carte", label: t("finance.subscriptions.modePaiement.carte") },
            { value: "sepa", label: t("finance.subscriptions.modePaiement.sepa") },
        ]);
        // Subscription frequency is restricted to the 4 cadences the prototype exposes;
        // matches the Jira spec (Monthly / Quarterly / Biannual / Annual).
        const frequencyOptions = computed(() => [
            { value: "Monthly", label: t("finance.subscriptions.frequency.Monthly") },
            { value: "Quarterly", label: t("finance.subscriptions.frequency.Quarterly") },
            { value: "Biannual", label: t("finance.subscriptions.frequency.Biannual") },
            { value: "Annual", label: t("finance.subscriptions.frequency.Annual") },
        ]);
        // ─── Contact picker — browse buffer + server-side search per keystroke ─────────
        // The initial load only fetches the first 50 rows (browse-all buffer); typed
        // queries go to GET /contacts?search= (org-scoped for an Organisation donor). A
        // monotonic token discards a slow earlier response that would otherwise
        // overwrite a newer one (parity with DonationCreateModal).
        const contacts = ref([]);
        const contactSuggestions = ref([]);
        const contactsLoading = ref(false);
        // Unfiltered (or org-scoped) DB total — drives the "Voir tous les résultats (XX)"
        // footer for the empty-query browse-all case.
        const contactTotalCount = ref(0);
        // Server-side total for the current typed query (see `contactSeeAllCount`).
        const contactSearchTotal = ref(0);
        let contactsLoaded = false;
        function contactToOption(c) {
            const name = `${c.firstName} ${c.lastName}`.trim() || c.id;
            return { value: c.id, label: name };
        }
        let contactsLoadedForOrgId = undefined;
        async function loadContacts(organisationId) {
            if (contactsLoaded && contactsLoadedForOrgId === organisationId)
                return;
            // A fresh buffer context (locked-donor open, post-create refresh) resets the
            // search state: any in-flight search targets the previous context and its result
            // must not overwrite the new buffer, and the footer count reverts to browse mode.
            contactSearchSeq++;
            contactSearchQuery.value = "";
            contactsLoading.value = true;
            try {
                const res = await getContacts({ page: 1, pageSize: 50, organisationId });
                contacts.value = res.result;
                contactSuggestions.value = res.result.map(contactToOption);
                contactTotalCount.value = res.totalItems;
                contactsLoaded = true;
                contactsLoadedForOrgId = organisationId;
            }
            catch {
                toastError("contact.loadError");
            }
            finally {
                contactsLoading.value = false;
            }
        }
        function contactSearchOrgScope() {
            return form.donorType === "Organization" ? form.organizationId || undefined : undefined;
        }
        // Live search term, mirrored synchronously from @search so the footer's count can
        // be computed outside the slot and so a query superseded during the debounce
        // window is detected before its fetch fires.
        const contactSearchQuery = ref("");
        let contactSearchSeq = 0;
        const debouncedContactFetch = useDebounceFn(async (query) => {
            // Superseded while debouncing (newer keystroke, or query emptied) → the
            // pending fetch for the stale term must not fire at all.
            if (query !== contactSearchQuery.value)
                return;
            const requestId = ++contactSearchSeq;
            contactsLoading.value = true;
            try {
                const res = await getContacts({
                    search: query,
                    organisationId: contactSearchOrgScope(),
                    page: 1,
                    pageSize: 50,
                });
                if (requestId !== contactSearchSeq)
                    return;
                contactSuggestions.value = res.result.map(contactToOption);
                contactSearchTotal.value = res.totalItems;
            }
            catch {
                if (requestId === contactSearchSeq)
                    toastError("contact.loadError");
            }
            finally {
                if (requestId === contactSearchSeq)
                    contactsLoading.value = false;
            }
        }, 300);
        function onContactSearch(query) {
            const q = query.trim();
            contactSearchQuery.value = q;
            if (!q) {
                // Collapsed query: nothing left to search, so restore the browse buffer
                // synchronously (no debounce) and invalidate any in-flight response.
                contactSearchSeq++;
                contactsLoading.value = false;
                contactSuggestions.value = contacts.value.map(contactToOption);
                return;
            }
            debouncedContactFetch(q);
        }
        // Footer count: server total for the current query, DB total when browsing.
        const contactSeeAllCount = computed(() => contactSearchQuery.value ? contactSearchTotal.value : contactTotalCount.value);
        const contactSeeAllLabel = computed(() => seeAllLabel(contactSeeAllCount.value));
        const contactCreateLabel = (query) => t("finance.subscriptions.create.createAsNewContact", { name: query.trim() });
        // ─── Organisation picker — browse buffer + server-side search per keystroke ────
        // Typed queries go to GET /organisations?search= (case-insensitive substring on
        // the legal name) with the same monotonic out-of-order guard as the contact
        // picker (parity with DonationCreateModal).
        const organisations = ref([]);
        const organisationSuggestions = ref([]);
        const organisationsLoading = ref(false);
        // Unfiltered DB total — drives the "Voir tous les résultats (XX)" footer for the
        // empty-query browse-all case.
        const organisationTotalCount = ref(0);
        // Server-side total for the current typed query (see `organisationSeeAllCount`).
        const organisationSearchTotal = ref(0);
        let organisationsLoaded = false;
        function organisationToOption(o) {
            const name = o.mainInformation.legalName?.trim() || o.mainInformation.acronym?.trim() || o.id;
            return { value: o.id, label: name };
        }
        async function loadOrganisations() {
            if (organisationsLoaded)
                return;
            // A fresh buffer (post-create refresh) resets the search state: any in-flight
            // search result must not overwrite the new buffer, and the footer count
            // reverts to browse mode.
            organisationSearchSeq++;
            organisationSearchQuery.value = "";
            organisationsLoading.value = true;
            try {
                const res = await getOrganisations({ page: 1, pageSize: 50 });
                organisations.value = res.result;
                organisationSuggestions.value = res.result.map(organisationToOption);
                organisationTotalCount.value = res.totalItems;
                organisationsLoaded = true;
            }
            catch {
                toastError("organisationList.loadError");
            }
            finally {
                organisationsLoading.value = false;
            }
        }
        // Live search term, mirrored synchronously from @search so the footer's count can
        // be computed outside the slot and so a query superseded during the debounce
        // window is detected before its fetch fires.
        const organisationSearchQuery = ref("");
        let organisationSearchSeq = 0;
        const debouncedOrganisationFetch = useDebounceFn(async (query) => {
            // Superseded while debouncing (newer keystroke, or query emptied) → the
            // pending fetch for the stale term must not fire at all.
            if (query !== organisationSearchQuery.value)
                return;
            const requestId = ++organisationSearchSeq;
            organisationsLoading.value = true;
            try {
                const res = await searchOrganisations({ search: query, page: 1, pageSize: 50 });
                if (requestId !== organisationSearchSeq)
                    return;
                organisationSuggestions.value = res.result.map(organisationToOption);
                organisationSearchTotal.value = res.totalItems;
            }
            catch {
                if (requestId === organisationSearchSeq)
                    toastError("organisationList.loadError");
            }
            finally {
                if (requestId === organisationSearchSeq)
                    organisationsLoading.value = false;
            }
        }, 300);
        function onOrganisationSearch(query) {
            const q = query.trim();
            organisationSearchQuery.value = q;
            if (!q) {
                // Collapsed query: nothing left to search, so restore the browse buffer
                // synchronously (no debounce) and invalidate any in-flight response.
                organisationSearchSeq++;
                organisationsLoading.value = false;
                organisationSuggestions.value = organisations.value.map(organisationToOption);
                return;
            }
            debouncedOrganisationFetch(q);
        }
        // Footer count: server total for the current query, DB total when browsing.
        const organisationSeeAllCount = computed(() => organisationSearchQuery.value ? organisationSearchTotal.value : organisationTotalCount.value);
        const organisationSeeAllLabel = computed(() => seeAllLabel(organisationSeeAllCount.value));
        const organisationCreateLabel = (query) => t("finance.subscriptions.create.createAsNewOrganisation", { name: query.trim() });
        // ─── Campaign picker — shared data source (useCampaignOptions) ─────────────────
        // The composable owns the browse buffer, the debounced server-side
        // GET /campaigns?search= with its out-of-order guard, and the query-aware footer
        // count. `status: null` keeps this modal's historical behaviour: every campaign
        // status is offered, not the composable's Active-only default.
        const { campaigns, options: campaignSuggestions, loading: campaignsLoading, seeAllCount: campaignSeeAllCount, toOption: campaignToOption, load: loadCampaigns, search: onCampaignSearch, noteSelected: noteCampaignSelected, fetchAllItems: fetchAllCampaignItems, } = useCampaignOptions({ status: null });
        const campaignSeeAllLabel = computed(() => seeAllLabel(campaignSeeAllCount.value));
        // "See all" shows only when more than the dropdown's 5-row cap exist.
        function seeAllLabel(count) {
            return count > 5 ? t("finance.subscriptions.create.seeAllResults", { count }) : undefined;
        }
        // ─── Form state ───────────────────────────────────────────────────────────────
        function todayLocalIsoDate() {
            const d = new Date();
            const y = d.getFullYear();
            const m = String(d.getMonth() + 1).padStart(2, "0");
            const day = String(d.getDate()).padStart(2, "0");
            return `${y}-${m}-${day}`;
        }
        // V2 spec defaults — pre-fill the most common values so the user only has to
        // fill donor / amount / SEPA-or-Carte details on the happy path. Mirrors the
        // donation-modal pattern (Individual donor, ManualDonation form, Monthly).
        const FORM_DEFAULTS = {
            donorType: "Individual",
            contact: "",
            organizationId: "",
            // Cash (Numéraire) is the only nature OPDON accepts for an auto-debit
            // recurring subscription, but expose it so the submitted payload reflects
            // what the user actually saw — no silent hardcode at submit time.
            donationNature: "Cash",
            amountStr: "",
            modePaiement: "carte",
            reference: "",
            sepaHolder: "",
            iban: "",
            bic: "",
            startDate: "",
            endDate: "",
            frequency: "Monthly",
            donationForm: "ManualDonation",
            campaign: "",
        };
        const form = reactive({
            ...FORM_DEFAULTS,
            startDate: todayLocalIsoDate(),
        });
        // ─── Locked-donor context (fiche entry points, OP-6139) ───────────────────────
        function applyLockedDonor(locked) {
            if (locked.donorType === "Organization") {
                form.donorType = "Organization";
                organisationSuggestions.value = [{ value: locked.organisationId, label: locked.displayName }];
                organisationTotalCount.value = 1;
                organisationsLoaded = true;
                form.organizationId = locked.organisationId;
                // No org watcher here → load the referent contact list scoped to the org.
                contactsLoaded = false;
                loadContacts(locked.organisationId);
            }
            else {
                contactSuggestions.value = [{ value: locked.contactId, label: locked.displayName }];
                contactTotalCount.value = 1;
                contactsLoaded = true;
                form.contact = locked.contactId;
            }
        }
        // `immediate: true` so the modal also loads its data when it's already open on
        // first mount — e.g. SubscriptionListView opens it via `?action=new` and a
        // changes-only watcher would never fire, leaving every picker empty until the
        // user closes and reopens.
        watch(open, (isOpen) => {
            if (!isOpen)
                return;
            donationStore
                .loadCatalogs()
                .catch(() => toastError("finance.subscriptions.catalogs.loadError"));
            loadCampaigns();
            if (props.lockedDonor) {
                applyLockedDonor(props.lockedDonor);
            }
            else {
                loadContacts();
                loadOrganisations();
            }
        }, { immediate: true });
        // Display-format proxies for the two DatePickerInputs. `form.startDate` /
        // `form.endDate` remain ISO (yyyy-MM-dd) on the wire and in the payload.
        const startDateDisplay = useDateIsoBinding(toRef(form, "startDate"), computed(() => profileStore.profile?.dateFormat), locale);
        const endDateDisplay = useDateIsoBinding(toRef(form, "endDate"), computed(() => profileStore.profile?.dateFormat), locale);
        // Clear the organisation field when switching to Individual so it cannot leak into the
        // payload. The contact is kept across the switch: it is the donor for an Individual
        // subscription and the mandatory "Contact référent" for an Organisation one (OP-5843).
        watch(() => form.donorType, (donorType) => {
            if (donorType === "Individual") {
                form.organizationId = "";
                errors.organizationId = "";
            }
        });
        // Clear the mandatory-contact error as soon as a contact is chosen.
        watch(() => form.contact, (value) => {
            if (value.trim())
                errors.contact = "";
        });
        // Blur validation (OP-5843): empty contact + lose focus → "Contact is required."
        function onContactBlur() {
            errors.contact = form.contact.trim()
                ? ""
                : t("finance.subscriptions.validation.contactRequired");
        }
        // ─── Blur validation for remaining mandatory fields (OP-5858) ─────────────────
        watch(() => form.campaign, (value) => {
            if (!value.trim())
                return;
            errors.campaign = "";
            // handleSubmit resolves the campaignName snapshot from the composable's
            // `campaigns` buffer; a selection made from server-searched suggestions lives
            // outside it, so seed it or the name is silently dropped from the payload
            // (mirrors onAllCampaignsConfirm).
            noteCampaignSelected(value);
        });
        function onCampaignBlur() {
            errors.campaign = form.campaign.trim()
                ? ""
                : t("finance.subscriptions.validation.campaignRequired");
        }
        watch(() => form.donationNature, (value) => {
            if (value)
                errors.donationNature = "";
        });
        function onDonationNatureBlur() {
            errors.donationNature = form.donationNature
                ? ""
                : t("finance.subscriptions.validation.natureRequired");
        }
        watch(() => form.amountStr, (value) => {
            const n = parseFloat(value);
            if (value && !isNaN(n) && n > 0)
                errors.amount = "";
        });
        function onAmountBlur() {
            const n = parseFloat(form.amountStr);
            errors.amount =
                form.amountStr && !isNaN(n) && n > 0
                    ? ""
                    : t("finance.subscriptions.validation.amountPositive");
        }
        watch(() => form.modePaiement, (value) => {
            if (value)
                errors.paymentMethod = "";
        });
        function onPaymentMethodBlur() {
            errors.paymentMethod = form.modePaiement
                ? ""
                : t("finance.subscriptions.validation.paymentMethodRequired");
        }
        watch(() => form.reference, (value) => {
            if (value.trim())
                errors.reference = "";
        });
        function onReferenceBlur() {
            if (form.modePaiement !== "carte")
                return;
            errors.reference = form.reference.trim()
                ? ""
                : t("finance.subscriptions.validation.referenceRequired");
        }
        watch(() => form.sepaHolder, (value) => {
            if (value.trim())
                errors.sepaHolder = "";
        });
        function onSepaHolderBlur() {
            if (form.modePaiement !== "sepa")
                return;
            errors.sepaHolder = form.sepaHolder.trim()
                ? ""
                : t("finance.subscriptions.validation.sepaHolderRequired");
        }
        watch(() => form.iban, (value) => {
            if (value.trim())
                errors.iban = "";
        });
        function onIbanBlur() {
            if (form.modePaiement !== "sepa")
                return;
            errors.iban = form.iban.trim() ? "" : t("finance.subscriptions.validation.ibanRequired");
        }
        watch(() => form.bic, (value) => {
            if (value.trim())
                errors.bic = "";
        });
        function onBicBlur() {
            if (form.modePaiement !== "sepa")
                return;
            errors.bic = form.bic.trim() ? "" : t("finance.subscriptions.validation.bicRequired");
        }
        watch(() => form.frequency, (value) => {
            if (value)
                errors.frequency = "";
        });
        function onFrequencyBlur() {
            errors.frequency = form.frequency
                ? ""
                : t("finance.subscriptions.validation.frequencyRequired");
        }
        // startDate is driven by DatePickerInput which doesn't emit blur; use the same
        // watcher approach as DonationCreateModal: show error when cleared after having a value.
        watch(() => form.startDate, (value, oldValue) => {
            if (value) {
                errors.startDate = "";
            }
            else if (oldValue) {
                errors.startDate = t("finance.subscriptions.validation.startDateRequired");
            }
        });
        // Switching payment mode wipes the sub-fields of the other mode.
        watch(() => form.modePaiement, (mode) => {
            if (mode === "carte") {
                form.sepaHolder = "";
                form.iban = "";
                form.bic = "";
                errors.sepaHolder = "";
                errors.iban = "";
                errors.bic = "";
            }
            else {
                form.reference = "";
                errors.reference = "";
            }
        });
        const errors = reactive({
            donorType: "",
            contact: "",
            organizationId: "",
            campaign: "",
            donationNature: "",
            amount: "",
            paymentMethod: "",
            reference: "",
            sepaHolder: "",
            iban: "",
            bic: "",
            startDate: "",
            endDate: "",
            frequency: "",
        });
        function clearErrors() {
            errors.donorType = "";
            errors.contact = "";
            errors.organizationId = "";
            errors.campaign = "";
            errors.donationNature = "";
            errors.amount = "";
            errors.paymentMethod = "";
            errors.reference = "";
            errors.sepaHolder = "";
            errors.iban = "";
            errors.bic = "";
            errors.startDate = "";
            errors.endDate = "";
            errors.frequency = "";
        }
        // ─── isFormValid — gates the submit button (no side effects) ──────────────────
        // OP-7954: an Organisation subscription checks the org AND its referent contact; an Individual
        // one checks the contact, which IS the donor.
        const receiptInformation = useDonorReceiptInformation({
            contactId: computed(() => form.contact || undefined),
            organizationId: computed(() => form.donorType === "Organization" ? form.organizationId || undefined : undefined),
            enabled: open,
        });
        const isFormValid = computed(() => {
            if (!form.contact.trim())
                return false;
            if (form.donorType === "Organization" && !form.organizationId.trim())
                return false;
            // OP-7954: the donor must carry everything the receipt needs. "Not checked yet" and "could not
            // check" both count as not complete — the save would be refused anyway.
            if (!receiptInformation.isComplete.value)
                return false;
            if (!form.campaign.trim())
                return false;
            if (!form.donationNature)
                return false;
            const amount = parseFloat(form.amountStr);
            if (!form.amountStr || isNaN(amount) || amount <= 0)
                return false;
            if (!workspaceCurrency.value)
                return false;
            if (!form.modePaiement)
                return false;
            if (form.modePaiement === "carte" && !form.reference.trim())
                return false;
            if (form.modePaiement === "sepa") {
                if (!form.sepaHolder.trim())
                    return false;
                if (!form.iban.trim())
                    return false;
                if (!form.bic.trim())
                    return false;
            }
            if (!form.startDate)
                return false;
            if (!form.frequency)
                return false;
            return true;
        });
        function resetForm() {
            Object.assign(form, FORM_DEFAULTS, { startDate: todayLocalIsoDate() });
            clearErrors();
        }
        function validate() {
            let valid = true;
            clearErrors();
            if (!form.donorType) {
                errors.donorType = t("finance.subscriptions.validation.donorTypeRequired");
                valid = false;
            }
            // Organisation subscriptions also require the organisation itself.
            if (form.donorType === "Organization" && !form.organizationId.trim()) {
                errors.organizationId = t("finance.subscriptions.validation.donorRequired");
                valid = false;
            }
            // A contact is mandatory for both donor types (OP-5843): the donor for an Individual
            // subscription, the org's referent contact for an Organisation one.
            if (!form.contact.trim()) {
                errors.contact = t("finance.subscriptions.validation.contactRequired");
                valid = false;
            }
            if (!form.campaign.trim()) {
                errors.campaign = t("finance.subscriptions.validation.campaignRequired");
                valid = false;
            }
            if (!form.donationNature) {
                errors.donationNature = t("finance.subscriptions.validation.natureRequired");
                valid = false;
            }
            const amount = parseFloat(form.amountStr);
            if (!form.amountStr || isNaN(amount) || amount <= 0) {
                errors.amount = t("finance.subscriptions.validation.amountPositive");
                valid = false;
            }
            if (!form.modePaiement) {
                errors.paymentMethod = t("finance.subscriptions.validation.paymentMethodRequired");
                valid = false;
            }
            if (form.modePaiement === "carte" && !form.reference.trim()) {
                errors.reference = t("finance.subscriptions.validation.referenceRequired");
                valid = false;
            }
            if (form.modePaiement === "sepa") {
                if (!form.sepaHolder.trim()) {
                    errors.sepaHolder = t("finance.subscriptions.validation.sepaHolderRequired");
                    valid = false;
                }
                if (!form.iban.trim()) {
                    errors.iban = t("finance.subscriptions.validation.ibanRequired");
                    valid = false;
                }
                if (!form.bic.trim()) {
                    errors.bic = t("finance.subscriptions.validation.bicRequired");
                    valid = false;
                }
            }
            if (!form.startDate) {
                errors.startDate = t("finance.subscriptions.validation.startDateRequired");
                valid = false;
            }
            if (form.endDate && form.startDate && form.endDate <= form.startDate) {
                errors.endDate = t("finance.subscriptions.validation.endDateAfterStart");
                valid = false;
            }
            if (!form.frequency) {
                errors.frequency = t("finance.subscriptions.validation.frequencyRequired");
                valid = false;
            }
            return valid;
        }
        function handleCancel() {
            resetForm();
            open.value = false;
            emit("close");
        }
        // Resolves the picked donor's display name from the picker options (falling back to the
        // raw loaded entities), so it can be forwarded to OPDON as a write-time snapshot fallback.
        function resolveDonorDisplayName() {
            if (form.donorType === "Individual") {
                const id = form.contact.trim();
                if (!id)
                    return undefined;
                const fromSuggestions = contactSuggestions.value.find((o) => o.value === id)?.label;
                if (fromSuggestions)
                    return fromSuggestions;
                const contact = contacts.value.find((c) => c.id === id);
                return contact ? contactToOption(contact).label : undefined;
            }
            if (form.donorType === "Organization") {
                const id = form.organizationId.trim();
                if (!id)
                    return undefined;
                const fromSuggestions = organisationSuggestions.value.find((o) => o.value === id)?.label;
                if (fromSuggestions)
                    return fromSuggestions;
                const org = organisations.value.find((o) => o.id === id);
                return org ? organisationToOption(org).label : undefined;
            }
            return undefined;
        }
        // Resolves the referent contact's display name from the contact picker, forwarded so OPDON can
        // snapshot the "Contact référent" name (Organisation subscription) while its OPUS contact
        // snapshot catches up. Mirrors the Individual branch of resolveDonorDisplayName.
        function resolveContactDisplayName() {
            const id = form.contact.trim();
            if (!id)
                return undefined;
            const fromSuggestions = contactSuggestions.value.find((o) => o.value === id)?.label;
            if (fromSuggestions)
                return fromSuggestions;
            const contact = contacts.value.find((c) => c.id === id);
            return contact ? contactToOption(contact).label : undefined;
        }
        async function handleSubmit() {
            if (!validate())
                return;
            const currency = workspaceCurrency.value;
            if (!currency)
                return;
            // Mode de paiement → BFF PaymentMethodPublic.
            const paymentMethod = form.modePaiement === "carte" ? "Card" : "DirectDebit";
            // BFF DateTime column expects an ISO instant; anchor the picked calendar day
            // at 12:00 UTC so viewers in any reasonable timezone see the same date.
            const startInstant = `${form.startDate}T12:00:00Z`;
            // SEPA mandate details live in `note` for now — the subscription wire has no
            // dedicated IBAN/BIC columns, and `note` is RGPD-acceptable here because the
            // submitter explicitly typed the values into a creation form.
            const sepaNote = form.modePaiement === "sepa"
                ? [
                    form.sepaHolder.trim() && `Titulaire: ${form.sepaHolder.trim()}`,
                    form.iban.trim() && `IBAN: ${form.iban.trim()}`,
                    form.bic.trim() && `BIC: ${form.bic.trim()}`,
                ]
                    .filter(Boolean)
                    .join(" | ")
                : "";
            // The selected contact/org display name, forwarded so OPDON can persist a write-time
            // donor-name snapshot — otherwise the donor renders blank on list/detail until OPDON's
            // live OPUS snapshot resolves the new donor via Kafka.
            const donorDisplayName = resolveDonorDisplayName();
            // Referent contact name — sent for an Organisation subscription so OPDON can snapshot the
            // "Contact référent" while its OPUS contact snapshot catches up (the donor name above is the org's).
            const contactDisplayName = resolveContactDisplayName();
            // Campaign name snapshot — forwarded so OPDON can persist the name without a live OPINK lookup.
            const campaignMatch = form.campaign ? campaigns.value.find((c) => c.id === form.campaign) : undefined;
            const payload = {
                amount: parseFloat(form.amountStr),
                currency,
                frequency: form.frequency,
                paymentMethod,
                donationNature: form.donationNature,
                startDate: startInstant,
                donorType: form.donorType,
                // contactId is the contact to link, sent for BOTH donor types (OP-5843): the donor for an
                // Individual subscription, the mandatory referent for an Organisation one. OPDON keeps it out
                // of the donor-resolution cascade for orgs (the donor stays the org).
                ...(form.contact.trim() ? { contactId: form.contact.trim() } : {}),
                ...(form.donorType === "Organization" && form.organizationId.trim()
                    ? { organizationId: form.organizationId.trim() }
                    : {}),
                ...(donorDisplayName ? { donorDisplayName } : {}),
                // Referent contact name only matters for an Organisation subscription (the donor name is the org's).
                ...(form.donorType === "Organization" && contactDisplayName ? { contactDisplayName } : {}),
                ...(form.endDate ? { endDate: `${form.endDate}T12:00:00Z` } : {}),
                ...(form.donationForm ? { donationForm: form.donationForm } : {}),
                ...(form.campaign
                    ? { campaignId: form.campaign, ...(campaignMatch?.name ? { campaignName: campaignMatch.name } : {}) }
                    : {}),
                // Carte reference travels as the external subscription identifier so the
                // OPAY reconciliation flow can join later.
                ...(form.modePaiement === "carte" && form.reference.trim()
                    ? { externalSubscriptionId: form.reference.trim(), externalProvider: "Manual" }
                    : {}),
                ...(sepaNote ? { note: sepaNote } : {}),
            };
            try {
                const subscription = await store.createSubscription(payload);
                toastSuccess("finance.subscriptions.saveSuccess");
                resetForm();
                if (props.lockedDonor) {
                    // Opened from a fiche: stay on the fiche. Emit first so the tab's `@created`
                    // refetch runs while the fiche is still mounted, then close the modal.
                    emit("created", subscription.id);
                    open.value = false;
                }
                else {
                    // Global flow: navigate BEFORE closing so the SubscriptionListView watcher fires
                    // after the route has already changed — otherwise its router.replace (removing
                    // ?action=new) races with and cancels this push.
                    await router.push({
                        name: "SubscriptionDetail",
                        params: { subscriptionId: subscription.id },
                    });
                    open.value = false;
                    emit("created", subscription.id);
                }
            }
            catch (e) {
                // The pre-check runs on donor selection, but the record can change between that answer and this
                // save — or the DonorId path can resolve links the probe never saw. The backend then refuses
                // with the field list; showing the generic save error here would hide the one thing the user
                // needs to act on, and the detail view already names them (R71).
                const gaps = parseDonorInformationGaps(getApiErrorResult(e));
                if (gaps.length > 0) {
                    toastError("finance.subscriptions.saveDonorInformationMissing", {
                        details: gaps.map((gap) => describeDonorInformationGap(gap, t)).join(" · "),
                    });
                    return;
                }
                if (getApiErrorCode(e) === "Workspace.Currency.NotAvailable") {
                    toastError("finance.subscriptions.errors.workspaceCurrencyUnavailable");
                    return;
                }
                toastError("finance.subscriptions.saveError");
            }
        }
        // ─── Nested "Create new contact" sub-modal ───────────────────────────────────
        // Opened from the Contact picker's dropdown footer. `redirect-on-create="false"`
        // keeps us on the subscription form; on `created` we refresh the contact list and
        // pre-select the new contact so the subscription can be submitted immediately.
        const showCreateContactModal = ref(false);
        // Lazy-mount: CreateContactModal pulls in dedup/preview composables and lookups in
        // its own setup. Keep it unmounted until first opened so that machinery doesn't run
        // on every subscription-modal open.
        const createContactModalMounted = ref(false);
        watch(showCreateContactModal, (isOpen) => {
            if (isOpen)
                createContactModalMounted.value = true;
        });
        function openCreateContact() {
            showCreateContactModal.value = true;
        }
        // Org context handed to the nested CreateContactModal. For an Organisation subscription a
        // selected org forces the Professional type and locks the org field; with no org selected
        // `force-professional-type` still forces Professional while the org field stays editable.
        // Individual subscriptions have no org context, so these resolve to undefined.
        const contactModalOrgId = computed(() => form.donorType === "Organization" && form.organizationId ? form.organizationId : undefined);
        const contactModalOrgLabel = computed(() => {
            const id = contactModalOrgId.value;
            if (!id)
                return undefined;
            const fromBuffer = organisations.value.find((o) => o.id === id);
            if (fromBuffer)
                return organisationToOption(fromBuffer).label;
            return organisationSuggestions.value.find((o) => o.value === id)?.label;
        });
        async function onContactCreated(newId, _relationTitle, name, organisation) {
            showCreateContactModal.value = false;
            // Organisation subscription whose Raison Sociale was still empty: adopt the org the user
            // linked the referent contact to, so the donor org stays consistent with the contact.
            // Seed the picker label first (mirrors onOrganisationCreated) so the AutoComplete renders
            // the name instead of the raw GUID, then loadContacts below is correctly org-filtered.
            if (form.donorType === "Organization" && !form.organizationId && organisation?.id) {
                if (!organisationSuggestions.value.some((o) => o.value === organisation.id)) {
                    organisationSuggestions.value = [
                        { value: organisation.id, label: organisation.label?.trim() || organisation.id },
                        ...organisationSuggestions.value,
                    ];
                }
                form.organizationId = organisation.id;
            }
            contactsLoaded = false;
            const organisationId = form.donorType === "Organization" ? form.organizationId || undefined : undefined;
            await loadContacts(organisationId);
            // The (org-filtered) reload only fetches the first 50 contacts; on an org with more than
            // that the freshly-created contact can fall outside the buffer, leaving the picker no
            // matching option (raw GUID). Seed both the picker options and the contacts buffer with
            // the emitted name so the label resolves and the referent display name survives regardless.
            if (name && !contacts.value.some((c) => c.id === newId)) {
                contacts.value = [
                    {
                        id: newId,
                        salutation: "",
                        firstName: name.firstName,
                        lastName: name.lastName,
                        status: "",
                        addresses: [],
                        communicationChannels: [],
                        organisationRoles: [],
                        categories: [],
                        tags: [],
                    },
                    ...contacts.value,
                ];
            }
            if (!contactSuggestions.value.some((o) => o.value === newId)) {
                const label = name ? `${name.firstName} ${name.lastName}`.trim() || newId : newId;
                contactSuggestions.value = [{ value: newId, label }, ...contactSuggestions.value];
            }
            form.contact = newId;
        }
        // ─── Nested "Create new organisation" sub-modal ──────────────────────────────
        // Opened from the Raison Sociale picker's dropdown footer. After
        // CreateOrganisationModal emits `created`, refresh the org list and pre-select
        // the new entity in the subscription form. Lazy-mounted so its setup doesn't
        // run on every subscription-modal open.
        const showCreateOrgModal = ref(false);
        const createOrgModalMounted = ref(false);
        watch(showCreateOrgModal, (isOpen) => {
            if (isOpen)
                createOrgModalMounted.value = true;
        });
        function openCreateOrg() {
            showCreateOrgModal.value = true;
        }
        async function onOrganisationCreated(newId) {
            showCreateOrgModal.value = false;
            organisationsLoaded = false;
            await loadOrganisations();
            form.organizationId = newId;
        }
        // ─── "Voir tous les résultats" full-list browsers ─────────────────────────────
        // Each picker's dropdown caps at 50 rows; clicking "Voir tous les résultats" opens
        // a browser that lazy-fetches a larger buffer and filters it by the query the user
        // had typed, so matches beyond that ceiling are reachable. On confirm the chosen id
        // is written to the form AND the picker buffer + suggestions are seeded so the label
        // (and the write-time display-name snapshot built in handleSubmit) resolve instead
        // of leaving a raw GUID. Mirrors DonationCreateModal.
        // Organisation browser. The typed query goes to the SERVER (same GET
        // /organisations?search= as the dropdown), so the listed matches are not limited
        // by any client-side buffer. Paged because the API caps PageSize at 100; the cap
        // bounds the empty-query browse-all fetch + render (the modal is not virtualized).
        const showAllOrgsModal = ref(false);
        const allOrgsQuery = ref("");
        const allOrgsList = ref([]);
        const allOrgsLoading = ref(false);
        const allOrgsError = ref(false);
        const allOrgsOptions = computed(() => allOrgsList.value.map(organisationToOption));
        async function openAllOrgsModal(query) {
            allOrgsQuery.value = query;
            showAllOrgsModal.value = true;
            allOrgsError.value = false;
            allOrgsLoading.value = true;
            try {
                const all = [];
                let page = 1;
                let total = Infinity;
                const search = query.trim() || undefined;
                while (all.length < total && all.length < ALL_ORGS_FETCH_MAX) {
                    // An empty query must keep using the plain list endpoint: searchOrganisations
                    // without a `search` term routes to the saved-filter search, not the browse list.
                    const res = search
                        ? await searchOrganisations({ search, page, pageSize: ORGS_FETCH_PAGE_SIZE })
                        : await getOrganisations({ page, pageSize: ORGS_FETCH_PAGE_SIZE });
                    all.push(...res.result);
                    total = res.totalItems;
                    if (res.result.length === 0)
                        break;
                    page++;
                }
                allOrgsList.value = all.slice(0, ALL_ORGS_FETCH_MAX);
            }
            catch {
                allOrgsList.value = [];
                allOrgsError.value = true;
            }
            finally {
                allOrgsLoading.value = false;
            }
        }
        function onAllOrgsConfirm(option) {
            form.organizationId = option.value;
            const selected = allOrgsList.value.find((o) => o.id === option.value);
            if (selected && !organisations.value.some((o) => o.id === selected.id)) {
                organisations.value = [selected, ...organisations.value];
            }
            organisationSuggestions.value = [
                option,
                ...organisationSuggestions.value.filter((o) => o.value !== option.value),
            ];
        }
        // Contact browser. The query is forwarded to the SERVER (same GET /contacts?search=
        // as the dropdown, org-scoped for an Organisation subscription), so the listed
        // matches are not limited by any client-side buffer. Paged because the contacts API
        // caps PageSize at 100; the hard ceiling keeps the non-virtualized modal from
        // rendering thousands of rows on an empty-query browse.
        const showAllContactsModal = ref(false);
        const allContactsQuery = ref("");
        const allContactsList = ref([]);
        const allContactsLoading = ref(false);
        const allContactsError = ref(false);
        const allContactsOptions = computed(() => allContactsList.value.map(contactToOption));
        async function openAllContactsModal(query) {
            allContactsQuery.value = query;
            showAllContactsModal.value = true;
            allContactsError.value = false;
            allContactsLoading.value = true;
            try {
                const all = [];
                let page = 1;
                let total = Infinity;
                const search = query.trim() || undefined;
                const organisationId = contactSearchOrgScope();
                while (all.length < total && all.length < ALL_CONTACTS_FETCH_MAX) {
                    const res = await getContacts({ page, pageSize: CONTACTS_FETCH_PAGE_SIZE, organisationId, search });
                    all.push(...res.result);
                    total = res.totalItems;
                    if (res.result.length === 0)
                        break;
                    page++;
                }
                allContactsList.value = all.slice(0, ALL_CONTACTS_FETCH_MAX);
            }
            catch {
                allContactsList.value = [];
                allContactsError.value = true;
            }
            finally {
                allContactsLoading.value = false;
            }
        }
        function onAllContactsConfirm(option) {
            form.contact = option.value;
            const selected = allContactsList.value.find((c) => c.id === option.value);
            if (selected && !contacts.value.some((c) => c.id === selected.id)) {
                contacts.value = [selected, ...contacts.value];
            }
            contactSuggestions.value = [
                option,
                ...contactSuggestions.value.filter((o) => o.value !== option.value),
            ];
        }
        // Campaign browser. The typed query goes to the SERVER (same GET /campaigns?search=
        // as the dropdown, via the composable's capped paged fetch), so the listed matches
        // are not limited by any client-side buffer.
        const showAllCampaignsModal = ref(false);
        const allCampaignsQuery = ref("");
        const allCampaignsList = ref([]);
        // Server total for the query — the title's count. The fetched list is capped, so
        // its length can undercount what the footer button advertised.
        const allCampaignsTotal = ref(0);
        const allCampaignsLoading = ref(false);
        const allCampaignsError = ref(false);
        const allCampaignsOptions = computed(() => allCampaignsList.value.map(campaignToOption));
        async function openAllCampaignsModal(query) {
            allCampaignsQuery.value = query;
            showAllCampaignsModal.value = true;
            allCampaignsError.value = false;
            allCampaignsLoading.value = true;
            try {
                const { items, totalCount } = await fetchAllCampaignItems(query);
                allCampaignsList.value = items;
                allCampaignsTotal.value = totalCount;
            }
            catch {
                allCampaignsList.value = [];
                allCampaignsTotal.value = 0;
                allCampaignsError.value = true;
            }
            finally {
                allCampaignsLoading.value = false;
            }
        }
        function onAllCampaignsConfirm(option) {
            form.campaign = option.value;
            const selected = allCampaignsList.value.find((c) => c.id === option.value);
            if (selected && !campaigns.value.some((c) => c.id === selected.id)) {
                campaigns.value = [selected, ...campaigns.value];
            }
            campaignSuggestions.value = [
                option,
                ...campaignSuggestions.value.filter((o) => o.value !== option.value),
            ];
        }
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock(_Fragment, null, [
                _createVNode(_unref(Modal), {
                    modelValue: open.value,
                    "onUpdate:modelValue": [
                        _cache[17] || (_cache[17] = ($event) => ((open).value = $event)),
                        _cache[18] || (_cache[18] = (v) => !v && handleCancel())
                    ],
                    size: "lg",
                    "footer-position": "split",
                    "header-position": "center",
                    "show-close-button": !_unref(store).isSaving,
                    "show-header-border": false,
                    "show-footer-border": false,
                    "header-class": "pt-2!",
                    dismissable: !_unref(store).isSaving,
                    "close-on-escape": !_unref(store).isSaving,
                    "aria-labelledby": _unref(titleId),
                    "aria-describedby": _unref(descriptionId)
                }, {
                    header: _withCtx(() => [
                        _createElementVNode("div", _hoisted_1, [
                            _createElementVNode("span", {
                                id: _unref(titleId),
                                class: "text-xl font-bold text-(--text-text-black-default) dark:text-(--text-text-white-default)"
                            }, _toDisplayString(_ctx.$t("finance.subscriptions.create.title")), 9 /* TEXT, PROPS */, _hoisted_2),
                            _createElementVNode("div", {
                                id: _unref(descriptionId),
                                class: "flex flex-col gap-0.5"
                            }, [
                                _createElementVNode("span", _hoisted_4, _toDisplayString(_ctx.$t("finance.subscriptions.create.subtitle")), 1 /* TEXT */),
                                _createElementVNode("span", _hoisted_5, _toDisplayString(_ctx.$t("finance.subscriptions.create.requiredFieldsHint")), 1 /* TEXT */)
                            ], 8 /* PROPS */, _hoisted_3)
                        ])
                    ]),
                    footer: _withCtx(() => [
                        _createVNode(_unref(Button), {
                            variant: "secondary",
                            label: _ctx.$t('common.cancel'),
                            disabled: _unref(store).isSaving,
                            onClick: handleCancel
                        }, null, 8 /* PROPS */, ["label", "disabled"]),
                        _createVNode(_unref(Button), {
                            variant: "primary",
                            "icon-left": "ri-check-line",
                            label: _ctx.$t('finance.subscriptions.create.submit'),
                            disabled: !isFormValid.value || _unref(store).isSaving,
                            loading: _unref(store).isSaving,
                            "data-testid": "subscription-create-submit",
                            onClick: handleSubmit
                        }, null, 8 /* PROPS */, ["label", "disabled", "loading"])
                    ]),
                    default: _withCtx(() => [
                        _createElementVNode("div", _hoisted_6, [
                            _createVNode(DonorReceiptInformationNotice, {
                                gaps: _unref(receiptInformation).issuerGaps.value,
                                unavailable: false,
                                "heading-key": "finance.donations.create.receiptInformation.workspaceTitle"
                            }, null, 8 /* PROPS */, ["gaps"]),
                            _createElementVNode("div", _hoisted_7, [
                                _createElementVNode("div", _hoisted_8, [
                                    _createElementVNode("h3", _hoisted_9, _toDisplayString(_ctx.$t("finance.subscriptions.create.sectionDonor")), 1 /* TEXT */),
                                    _createVNode(_unref(SelectInput), {
                                        modelValue: form.donorType,
                                        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => ((form.donorType) = $event)),
                                        label: _ctx.$t('finance.subscriptions.fields.donorType'),
                                        placeholder: _ctx.$t('finance.subscriptions.fields.donorTypePlaceholder'),
                                        options: _unref(donorTypeOptions),
                                        required: true,
                                        disabled: !!props.lockedDonor,
                                        "full-width": ""
                                    }, null, 8 /* PROPS */, ["modelValue", "label", "placeholder", "options", "disabled"]),
                                    (form.donorType === 'Organization')
                                        ? (_openBlock(), _createBlock(_unref(AutoComplete), _mergeProps({ key: 0 }, _unref(buildAutoCompleteLabels)(_unref(t)), {
                                            modelValue: form.organizationId,
                                            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event) => ((form.organizationId) = $event)),
                                            label: _ctx.$t('finance.subscriptions.fields.orgName'),
                                            placeholder: _ctx.$t('finance.subscriptions.fields.orgNamePlaceholder'),
                                            options: organisationSuggestions.value,
                                            loading: organisationsLoading.value,
                                            error: errors.organizationId,
                                            required: true,
                                            "icon-left": '',
                                            "icon-right": '',
                                            disabled: props.lockedDonor?.donorType === 'Organization',
                                            "data-testid": "subscription-create-org-picker",
                                            "full-width": "",
                                            "see-all-label": organisationSeeAllLabel.value,
                                            "create-label": organisationCreateLabel,
                                            onSearch: onOrganisationSearch,
                                            onSeeAll: openAllOrgsModal,
                                            onCreate: _cache[2] || (_cache[2] = ($event) => (openCreateOrg()))
                                        }), null, 16 /* FULL_PROPS */, ["modelValue", "label", "placeholder", "options", "loading", "error", "disabled", "see-all-label"]))
                                        : _createCommentVNode("v-if", true),
                                    _createVNode(_unref(AutoComplete), _mergeProps(_unref(buildAutoCompleteLabels)(_unref(t)), {
                                        modelValue: form.contact,
                                        "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event) => ((form.contact) = $event)),
                                        label: _ctx.$t('finance.subscriptions.fields.contactName'),
                                        placeholder: _ctx.$t('finance.subscriptions.fields.contactNamePlaceholder'),
                                        options: contactSuggestions.value,
                                        loading: contactsLoading.value,
                                        error: errors.contact,
                                        required: true,
                                        "icon-left": '',
                                        "icon-right": '',
                                        disabled: props.lockedDonor?.donorType === 'Individual',
                                        "data-testid": "subscription-create-contact-picker",
                                        "full-width": "",
                                        "see-all-label": contactSeeAllLabel.value,
                                        "create-label": contactCreateLabel,
                                        onSearch: onContactSearch,
                                        onBlur: onContactBlur,
                                        onSeeAll: openAllContactsModal,
                                        onCreate: _cache[4] || (_cache[4] = ($event) => (openCreateContact()))
                                    }), null, 16 /* FULL_PROPS */, ["modelValue", "label", "placeholder", "options", "loading", "error", "disabled", "see-all-label"]),
                                    _createVNode(DonorReceiptInformationNotice, {
                                        class: "mb-5",
                                        gaps: _unref(receiptInformation).donorGaps.value,
                                        unavailable: _unref(receiptInformation).unavailable.value,
                                        "contact-id": form.contact || undefined,
                                        "organization-id": form.donorType === 'Organization' ? form.organizationId || undefined : undefined,
                                        "on-retry": _unref(receiptInformation).retry
                                    }, null, 8 /* PROPS */, ["gaps", "unavailable", "contact-id", "organization-id", "on-retry"]),
                                    _createVNode(_unref(AutoComplete), _mergeProps(_unref(buildAutoCompleteLabels)(_unref(t)), {
                                        modelValue: form.campaign,
                                        "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event) => ((form.campaign) = $event)),
                                        label: _ctx.$t('finance.subscriptions.fields.campaign'),
                                        placeholder: _ctx.$t('finance.subscriptions.fields.campaignPlaceholder'),
                                        options: _unref(campaignSuggestions),
                                        loading: _unref(campaignsLoading),
                                        error: errors.campaign,
                                        required: true,
                                        "icon-left": '',
                                        "icon-right": '',
                                        "data-testid": "subscription-create-campaign-picker",
                                        "full-width": "",
                                        "see-all-label": campaignSeeAllLabel.value,
                                        onSearch: _unref(onCampaignSearch),
                                        onBlur: onCampaignBlur,
                                        onSeeAll: openAllCampaignsModal
                                    }), null, 16 /* FULL_PROPS */, ["modelValue", "label", "placeholder", "options", "loading", "error", "see-all-label", "onSearch"])
                                ]),
                                _createElementVNode("div", _hoisted_10, [
                                    _createElementVNode("h3", _hoisted_11, _toDisplayString(_ctx.$t("finance.subscriptions.create.sectionPayment")), 1 /* TEXT */),
                                    _createVNode(_unref(SelectInput), {
                                        modelValue: form.donationNature,
                                        "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event) => ((form.donationNature) = $event)),
                                        label: _ctx.$t('finance.subscriptions.fields.donationNature'),
                                        options: _unref(donationNatureOptions),
                                        error: errors.donationNature,
                                        required: true,
                                        "data-testid": "subscription-create-donation-nature",
                                        "full-width": "",
                                        onBlur: onDonationNatureBlur
                                    }, null, 8 /* PROPS */, ["modelValue", "label", "options", "error"]),
                                    _createVNode(_unref(NumberInput), {
                                        modelValue: form.amountStr,
                                        "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event) => ((form.amountStr) = $event)),
                                        label: _ctx.$t('finance.subscriptions.fields.amount'),
                                        placeholder: _ctx.$t('finance.subscriptions.fields.amountPlaceholder'),
                                        hint: _ctx.$t('finance.subscriptions.fields.amountHint'),
                                        error: _unref(workspaceCurrencyUnavailable)
                                            ? _ctx.$t('finance.subscriptions.errors.workspaceCurrencyUnavailable')
                                            : errors.amount,
                                        required: true,
                                        prefix: amountPrefix.value,
                                        disabled: !_unref(workspaceCurrency),
                                        min: 0,
                                        step: 0.01,
                                        "data-testid": "subscription-create-amount",
                                        "full-width": "",
                                        onBlur: onAmountBlur
                                    }, null, 8 /* PROPS */, ["modelValue", "label", "placeholder", "hint", "error", "prefix", "disabled"]),
                                    _createVNode(_unref(SelectInput), {
                                        modelValue: form.modePaiement,
                                        "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event) => ((form.modePaiement) = $event)),
                                        label: _ctx.$t('finance.subscriptions.fields.paymentMethod'),
                                        options: modePaiementOptions.value,
                                        error: errors.paymentMethod,
                                        required: true,
                                        "data-testid": "subscription-create-payment-method",
                                        "full-width": "",
                                        onBlur: onPaymentMethodBlur
                                    }, null, 8 /* PROPS */, ["modelValue", "label", "options", "error"]),
                                    (form.modePaiement === 'carte')
                                        ? (_openBlock(), _createBlock(_unref(TextInput), {
                                            key: 0,
                                            modelValue: form.reference,
                                            "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event) => ((form.reference) = $event)),
                                            label: _ctx.$t('finance.subscriptions.fields.reference'),
                                            placeholder: _ctx.$t('finance.subscriptions.fields.referencePlaceholder'),
                                            error: errors.reference,
                                            required: true,
                                            "data-testid": "subscription-create-reference",
                                            "full-width": "",
                                            onBlur: onReferenceBlur
                                        }, null, 8 /* PROPS */, ["modelValue", "label", "placeholder", "error"]))
                                        : _createCommentVNode("v-if", true),
                                    (form.modePaiement === 'sepa')
                                        ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                            _createVNode(_unref(TextInput), {
                                                modelValue: form.sepaHolder,
                                                "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event) => ((form.sepaHolder) = $event)),
                                                label: _ctx.$t('finance.subscriptions.fields.sepaHolder'),
                                                placeholder: _ctx.$t('finance.subscriptions.fields.sepaHolderPlaceholder'),
                                                error: errors.sepaHolder,
                                                required: true,
                                                "data-testid": "subscription-create-sepa-holder",
                                                "full-width": "",
                                                onBlur: onSepaHolderBlur
                                            }, null, 8 /* PROPS */, ["modelValue", "label", "placeholder", "error"]),
                                            _createVNode(_unref(TextInput), {
                                                modelValue: form.iban,
                                                "onUpdate:modelValue": _cache[11] || (_cache[11] = ($event) => ((form.iban) = $event)),
                                                label: _ctx.$t('finance.subscriptions.fields.iban'),
                                                placeholder: _ctx.$t('finance.subscriptions.fields.ibanPlaceholder'),
                                                error: errors.iban,
                                                required: true,
                                                "data-testid": "subscription-create-iban",
                                                "full-width": "",
                                                onBlur: onIbanBlur
                                            }, null, 8 /* PROPS */, ["modelValue", "label", "placeholder", "error"]),
                                            _createVNode(_unref(TextInput), {
                                                modelValue: form.bic,
                                                "onUpdate:modelValue": _cache[12] || (_cache[12] = ($event) => ((form.bic) = $event)),
                                                label: _ctx.$t('finance.subscriptions.fields.bic'),
                                                placeholder: _ctx.$t('finance.subscriptions.fields.bicPlaceholder'),
                                                error: errors.bic,
                                                required: true,
                                                "data-testid": "subscription-create-bic",
                                                "full-width": "",
                                                onBlur: onBicBlur
                                            }, null, 8 /* PROPS */, ["modelValue", "label", "placeholder", "error"])
                                        ], 64 /* STABLE_FRAGMENT */))
                                        : _createCommentVNode("v-if", true)
                                ])
                            ]),
                            _createElementVNode("div", _hoisted_12, [
                                _createElementVNode("div", _hoisted_13, [
                                    _createVNode(_unref(DatePickerInput), {
                                        modelValue: _unref(startDateDisplay),
                                        "onUpdate:modelValue": _cache[13] || (_cache[13] = ($event) => (_isRef(startDateDisplay) ? (startDateDisplay).value = $event : null)),
                                        label: _ctx.$t('finance.subscriptions.fields.firstDebitDate'),
                                        error: errors.startDate,
                                        required: true,
                                        format: dateFormat.value,
                                        language: _unref(locale),
                                        "week-start": 1,
                                        "full-width": ""
                                    }, null, 8 /* PROPS */, ["modelValue", "label", "error", "format", "language"]),
                                    _createVNode(_unref(SelectInput), {
                                        modelValue: form.frequency,
                                        "onUpdate:modelValue": _cache[14] || (_cache[14] = ($event) => ((form.frequency) = $event)),
                                        label: _ctx.$t('finance.subscriptions.fields.frequency'),
                                        placeholder: _ctx.$t('finance.subscriptions.fields.frequencyPlaceholder'),
                                        options: frequencyOptions.value,
                                        error: errors.frequency,
                                        required: true,
                                        "data-testid": "subscription-create-frequency",
                                        "full-width": "",
                                        onBlur: onFrequencyBlur
                                    }, null, 8 /* PROPS */, ["modelValue", "label", "placeholder", "options", "error"]),
                                    _createVNode(_unref(DatePickerInput), {
                                        modelValue: _unref(endDateDisplay),
                                        "onUpdate:modelValue": _cache[15] || (_cache[15] = ($event) => (_isRef(endDateDisplay) ? (endDateDisplay).value = $event : null)),
                                        label: _ctx.$t('finance.subscriptions.fields.endDate'),
                                        error: errors.endDate,
                                        format: dateFormat.value,
                                        language: _unref(locale),
                                        "week-start": 1,
                                        "full-width": ""
                                    }, null, 8 /* PROPS */, ["modelValue", "label", "error", "format", "language"]),
                                    _createVNode(_unref(SelectInput), {
                                        modelValue: form.donationForm,
                                        "onUpdate:modelValue": _cache[16] || (_cache[16] = ($event) => ((form.donationForm) = $event)),
                                        label: _ctx.$t('finance.subscriptions.fields.donationForm'),
                                        placeholder: _ctx.$t('finance.subscriptions.fields.donationFormPlaceholder'),
                                        options: _unref(donationFormOptions),
                                        "full-width": ""
                                    }, null, 8 /* PROPS */, ["modelValue", "label", "placeholder", "options"])
                                ])
                            ])
                        ])
                    ]),
                    _: 1 /* STABLE */
                }, 8 /* PROPS */, ["modelValue", "show-close-button", "dismissable", "close-on-escape", "aria-labelledby", "aria-describedby"]),
                (createContactModalMounted.value)
                    ? (_openBlock(), _createBlock(CreateContactModal, {
                        key: 0,
                        modelValue: showCreateContactModal.value,
                        "onUpdate:modelValue": _cache[19] || (_cache[19] = ($event) => ((showCreateContactModal).value = $event)),
                        "redirect-on-create": false,
                        "initial-organisation-id": contactModalOrgId.value,
                        "initial-organisation-label": contactModalOrgLabel.value,
                        "force-professional-type": form.donorType === 'Organization',
                        onCreated: onContactCreated
                    }, null, 8 /* PROPS */, ["modelValue", "initial-organisation-id", "initial-organisation-label", "force-professional-type"]))
                    : _createCommentVNode("v-if", true),
                (createOrgModalMounted.value)
                    ? (_openBlock(), _createBlock(CreateOrganisationModal, {
                        key: 1,
                        modelValue: showCreateOrgModal.value,
                        "onUpdate:modelValue": _cache[20] || (_cache[20] = ($event) => ((showCreateOrgModal).value = $event)),
                        "redirect-on-create": false,
                        onCreated: onOrganisationCreated
                    }, null, 8 /* PROPS */, ["modelValue"]))
                    : _createCommentVNode("v-if", true),
                _createVNode(_unref(AutoCompleteAllResultsModal), {
                    modelValue: showAllOrgsModal.value,
                    "onUpdate:modelValue": _cache[21] || (_cache[21] = ($event) => ((showAllOrgsModal).value = $event)),
                    options: allOrgsOptions.value,
                    loading: allOrgsLoading.value,
                    error: allOrgsError.value,
                    title: _ctx.$t('finance.subscriptions.create.allResults.title', {
                        count: allOrgsOptions.value.length,
                        query: allOrgsQuery.value,
                    }),
                    subtitle: _ctx.$t('finance.subscriptions.create.allResults.subtitleOrganisation'),
                    "close-label": _ctx.$t('finance.subscriptions.create.allResults.close'),
                    "select-label": _ctx.$t('finance.subscriptions.create.allResults.select'),
                    "load-error-label": _ctx.$t('finance.subscriptions.create.allResults.loadError'),
                    "empty-label": _ctx.$t('finance.subscriptions.create.allResults.empty'),
                    onConfirm: onAllOrgsConfirm
                }, null, 8 /* PROPS */, ["modelValue", "options", "loading", "error", "title", "subtitle", "close-label", "select-label", "load-error-label", "empty-label"]),
                _createVNode(_unref(AutoCompleteAllResultsModal), {
                    modelValue: showAllContactsModal.value,
                    "onUpdate:modelValue": _cache[22] || (_cache[22] = ($event) => ((showAllContactsModal).value = $event)),
                    options: allContactsOptions.value,
                    loading: allContactsLoading.value,
                    error: allContactsError.value,
                    title: _ctx.$t('finance.subscriptions.create.allResults.title', {
                        count: allContactsOptions.value.length,
                        query: allContactsQuery.value,
                    }),
                    subtitle: _ctx.$t('finance.subscriptions.create.allResults.subtitleContact'),
                    "close-label": _ctx.$t('finance.subscriptions.create.allResults.close'),
                    "select-label": _ctx.$t('finance.subscriptions.create.allResults.select'),
                    "load-error-label": _ctx.$t('finance.subscriptions.create.allResults.loadError'),
                    "empty-label": _ctx.$t('finance.subscriptions.create.allResults.empty'),
                    onConfirm: onAllContactsConfirm
                }, null, 8 /* PROPS */, ["modelValue", "options", "loading", "error", "title", "subtitle", "close-label", "select-label", "load-error-label", "empty-label"]),
                _createVNode(_unref(AutoCompleteAllResultsModal), {
                    modelValue: showAllCampaignsModal.value,
                    "onUpdate:modelValue": _cache[23] || (_cache[23] = ($event) => ((showAllCampaignsModal).value = $event)),
                    options: allCampaignsOptions.value,
                    loading: allCampaignsLoading.value,
                    error: allCampaignsError.value,
                    title: _ctx.$t('finance.subscriptions.create.allResults.title', {
                        count: allCampaignsTotal.value,
                        query: allCampaignsQuery.value,
                    }),
                    subtitle: _ctx.$t('finance.subscriptions.create.allResults.subtitleCampaign'),
                    "close-label": _ctx.$t('finance.subscriptions.create.allResults.close'),
                    "select-label": _ctx.$t('finance.subscriptions.create.allResults.select'),
                    "load-error-label": _ctx.$t('finance.subscriptions.create.allResults.loadError'),
                    "empty-label": _ctx.$t('finance.subscriptions.create.allResults.empty'),
                    onConfirm: onAllCampaignsConfirm
                }, null, 8 /* PROPS */, ["modelValue", "options", "loading", "error", "title", "subtitle", "close-label", "select-label", "load-error-label", "empty-label"])
            ], 64 /* STABLE_FRAGMENT */));
        };
    }
});
